class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;
    this.hoverTimeout = null;
    // Hover intent, and a close that waits for the panel to animate out.
    this._openTimer = null;
    this._closeTimer = null;
    this.OPEN_DELAY = 90;
    this.CLOSE_DURATION = 220;
    this.transparentWrapper = document.querySelector(
      ".header-wrapper--transparent",
    );

    // Pre-bind handlers so we can remove them later
    this._boundMouseEnter = this.onMouseEnter.bind(this);
    this._boundMouseLeave = this.onMouseLeave.bind(this);
    this._boundContentEnter = this.keepOpen.bind(this);
    this._boundContentLeave = this.onMouseLeave.bind(this);
    this._boundPreventClick = this.preventClick.bind(this);
    this._boundUpdateHeaderOffset = this.updateHeaderBottomPosition.bind(this);

    // Check if we're on desktop
    this.checkDevice();
    window.addEventListener("resize", this.checkDevice.bind(this));

    // Sync the animation class off the details' own toggle event, so it covers
    // every way the panel can open — hover, keyboard, or a native click — rather
    // than only the hover path.
    if (this.mainDetailsToggle.classList.contains("mega-menu")) {
      this.mainDetailsToggle.addEventListener("toggle", () => {
        if (!this.mainDetailsToggle.open) {
          this.content.classList.remove("menu-opening");
          return;
        }
        // Two frames: one for the browser to render the now-open content in its
        // hidden state, one for the change to it to be a transition.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.mainDetailsToggle.open) {
              this.content.classList.add("menu-opening");
            }
          });
        });
      });
    }

    // Add hover events for desktop
    if (this.isDesktop) {
      this.addHoverEvents();
    }
  }

  checkDevice() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;

    // If we switched between desktop and mobile, update event listeners
    if (wasDesktop !== this.isDesktop) {
      if (this.isDesktop) {
        this.addHoverEvents();
      } else {
        this.removeHoverEvents();
      }
    }
  }

  addHoverEvents() {
    // Only for mega menu type
    if (!this.mainDetailsToggle.classList.contains("mega-menu")) return;
    // Remove click events and add hover events for desktop
    this.mainDetailsToggle.addEventListener(
      "mouseenter",
      this._boundMouseEnter,
    );
    this.mainDetailsToggle.addEventListener(
      "mouseleave",
      this._boundMouseLeave,
    );
    this.content.addEventListener("mouseenter", this._boundContentEnter);
    this.content.addEventListener("mouseleave", this._boundContentLeave);

    // Prevent click from toggling on desktop
    this.mainDetailsToggle.addEventListener("click", this._boundPreventClick);

    // When a modal inside the panel closes, drop the panel too unless the
    // pointer is still over it (the close was blocked while the modal was up).
    const innerModal = this.content.querySelector("details-modal details");
    if (innerModal) {
      innerModal.addEventListener("toggle", () => {
        if (innerModal.open || !this.isDesktop) return;
        if (this.mainDetailsToggle.matches(":hover")) return;
        this.close();
        this.content.classList.remove("menu-opening");
      });
    }

    // Also toggle aria-expanded on focus/blur for keyboard users
    const summary = this.mainDetailsToggle.querySelector("summary");
    summary &&
      summary.addEventListener("focus", () => {
        if (!this.isDesktop) return;
        summary.setAttribute("aria-expanded", true);
      });
    summary &&
      summary.addEventListener("blur", () => {
        if (!this.isDesktop) return;
        summary.setAttribute("aria-expanded", false);
      });
  }

  removeHoverEvents() {
    if (!this.mainDetailsToggle.classList.contains("mega-menu")) return;
    this.mainDetailsToggle.removeEventListener(
      "mouseenter",
      this._boundMouseEnter,
    );
    this.mainDetailsToggle.removeEventListener(
      "mouseleave",
      this._boundMouseLeave,
    );
    this.content.removeEventListener("mouseenter", this._boundContentEnter);
    this.content.removeEventListener("mouseleave", this._boundContentLeave);
    this.mainDetailsToggle.removeEventListener(
      "click",
      this._boundPreventClick,
    );
  }

  onMouseEnter() {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    // A pending close is stale now the pointer is back.
    clearTimeout(this._closeTimer);
    this._closeTimer = null;

    // Opening on the first pixel of hover makes the nav twitch as the pointer
    // crosses it. A short intent delay settles that — but when another panel is
    // already open the same delay reads as lag, so swap immediately.
    const openElsewhere = document.querySelector(
      "header-menu details.mega-menu[open]",
    );
    const swapping = openElsewhere && openElsewhere !== this.mainDetailsToggle;

    clearTimeout(this._openTimer);
    this._openTimer = setTimeout(
      () => this.openPanel(),
      swapping ? 0 : this.OPEN_DELAY,
    );
  }

  openPanel() {
    // Update header position to avoid gaps/overlaps
    this.updateHeaderBottomPosition();

    // Open the menu
    this.mainDetailsToggle.setAttribute("open", "");
    const summary = this.mainDetailsToggle.querySelector("summary");
    summary && summary.setAttribute("aria-expanded", true);
    this.onToggle();

    // Normally the toggle listener adds the class a frame later. But coming back
    // during a pending close, `open` is still set, so setAttribute fires no
    // toggle event and the listener never runs — the panel would stay invisible
    // with nothing to show it again. Adding it here covers that; classList.add
    // is idempotent, so the two paths cannot conflict.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.mainDetailsToggle.open) {
          this.content.classList.add("menu-opening");
        }
      });
    });

    // If header is transparent, apply scrolled background while menu is open
    if (this.transparentWrapper) {
      this.transparentWrapper.classList.add("header-scrolled");
    }

    // Continuously keep header offset in sync while open
    window.addEventListener("scroll", this._boundUpdateHeaderOffset, {
      passive: true,
    });
    window.addEventListener("resize", this._boundUpdateHeaderOffset);
    // Double-check after next frame to account for any layout changes
    requestAnimationFrame(() => this.updateHeaderBottomPosition());
  }

  onMouseLeave() {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Cancel a pending open, so sweeping across the nav opens nothing.
    clearTimeout(this._openTimer);
    this._openTimer = null;

    // Set a timeout before closing to allow moving to content
    this.hoverTimeout = setTimeout(() => {
      // Keep the panel open while a modal inside it is open (e.g. the region
      // selector). That modal is a descendant, so closing the panel would take
      // it with it the moment the pointer moved onto the overlay.
      if (this.content.querySelector("details-modal details[open]")) {
        this.hoverTimeout = null;
        return;
      }
      this.close();
    }, 150);
  }

  keepOpen() {
    // Clear the timeout when hovering over the content
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    clearTimeout(this._closeTimer);
    this._closeTimer = null;
  }

  preventClick(event) {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Only prevent clicks on THIS menu's own summary, not on links or other
    // summaries inside the panel (e.g. the ship-to trigger that opens the
    // region modal, which must stay clickable).
    const summary = event.target.closest("summary");
    if (summary && summary.parentElement === this.mainDetailsToggle) {
      // Allow keyboard activation (detail === 0). Prevent mouse clicks only
      if (event.detail !== 0) {
        event.preventDefault();
      }
    }
  }

  onToggle() {
    if (!this.header) return;

    // Only update preventHide on mobile/tablet
    if (!this.isDesktop) {
      this.header.preventHide = this.mainDetailsToggle.open;
    }

    // Always refresh header position on toggle for desktop
    if (this.isDesktop) {
      this.updateHeaderBottomPosition();
    }
  }

  close(options) {
    const immediate = options && options.immediate;

    // Start the fade out first. Removing `open` in the same breath unrenders the
    // panel instantly — a closed <details> does not render its content — so the
    // panel used to vanish rather than animate away.
    this.content.classList.remove("menu-opening");

    clearTimeout(this._closeTimer);

    const finish = () => {
      this._closeTimer = null;
      this.mainDetailsToggle.removeAttribute("open");
      const summary = this.mainDetailsToggle.querySelector("summary");
      summary && summary.setAttribute("aria-expanded", false);
    };

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (immediate || reducedMotion) {
      finish();
    } else {
      this._closeTimer = setTimeout(finish, this.CLOSE_DURATION);
    }

    // Remove listeners keeping header offset in sync
    window.removeEventListener("scroll", this._boundUpdateHeaderOffset);
    window.removeEventListener("resize", this._boundUpdateHeaderOffset);

    // Only remove header-scrolled state if no other mega-menu is currently open/hovered
    if (this.transparentWrapper) {
      const anyOpen = document.querySelector("header-menu details[open]");
      const anyHover = document.querySelector(
        "header-menu details.mega-menu:hover",
      );
      if (!anyOpen && !anyHover) {
        // Allow transparent header logic to control this on scroll
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop || 0;
        if (scrollTop <= 50) {
          this.transparentWrapper.classList.remove("header-scrolled");
        }
      }
    }
  }

  updateHeaderBottomPosition() {
    if (!this.header) return;
    const rect = this.header.getBoundingClientRect();
    // Account for optional bottom border to avoid tiny overlaps
    const wrapper = this.header.closest(".header-wrapper") || this.header;
    const hasBorder = wrapper.classList?.contains(
      "header-wrapper--border-bottom",
    );
    const borderOffset = hasBorder ? 1 : 0;
    const bottom = Math.ceil(rect.bottom - borderOffset);
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${bottom}px`,
    );
  }
}

customElements.define("header-menu", HeaderMenu);
